import type { FC, MouseEventHandler } from 'react';
import { Show } from '@components/Show';
import { TooltipedIcon } from '@components/TooltipedIcon';
import { isBlockOrInlineAttachment } from '@features/attachments/utils/is-inline-attachment-or-block';
import {
  mdiCog,
  mdiCommentPlusOutline,
  mdiCommentProcessingOutline,
  mdiOpenInNew,
  mdiPencil,
  mdiTrashCanOutline,
} from '@mdi/js';
import type { ResourceBlock } from '@root/@types/base';
import { twMerge } from 'tailwind-merge';

import { iconClasses } from '../classes';
import { Container } from '../components/Container';
import { getId } from '../utils';

export interface ContractBlockActionsViewProps {
  block: ResourceBlock;
  commentCount: number;
  unresolvedCommentCount: number;
  isReviewStatus: boolean;
  showCommentsActions: boolean;
  isEditable: boolean;
  isDeletable: boolean;
  canViewInLibrary: boolean;
  isClause: boolean;
  isTemplate: boolean;
  onCommentClick: () => void;
  onEdit: MouseEventHandler<HTMLDivElement>;
  onDelete: MouseEventHandler;
  onViewInLibrary: () => void;
  onOpenVisibilityModal: () => void;
}

export const ContractBlockActionsView: FC<ContractBlockActionsViewProps> = ({
  block,
  commentCount,
  unresolvedCommentCount,
  isReviewStatus,
  showCommentsActions,
  isEditable,
  isDeletable,
  canViewInLibrary,
  isClause,
  isTemplate,
  onCommentClick,
  onEdit,
  onDelete,
  onViewInLibrary,
  onOpenVisibilityModal,
}) => {
  const { name, blockLibraryId } = block;

  const hasComments = commentCount > 0;
  const hasUnresolvedComments = unresolvedCommentCount > 0;
  const hasMoreThanNinetyNineComments = commentCount > 99;

  const hasCommentsActionAvailable = showCommentsActions && (hasComments || (!hasComments && isReviewStatus));
  const hasViewInLibraryActionAvailable = !!blockLibraryId && canViewInLibrary;

  const hasAnyActionAvailable =
    hasCommentsActionAvailable || isEditable || isDeletable || hasViewInLibraryActionAvailable;

  if (!hasAnyActionAvailable) {
    return null;
  }

  return (
    <Container alwaysVisible={showCommentsActions && hasUnresolvedComments}>
      <div className={'flex h-fit flex-col gap-3 transition-all duration-300 ease-in-out'}>
        <Show when={showCommentsActions}>
          <Show when={hasComments}>
            <div className="relative">
              <TooltipedIcon
                size={0.9}
                className={iconClasses({ alwaysVisible: hasUnresolvedComments })}
                data-testid={getId('view-comments', name)}
                path={mdiCommentProcessingOutline}
                onClick={onCommentClick}
                content="View comments"
              />
              <Show when={hasUnresolvedComments}>
                <div
                  className={twMerge(
                    'bg-primary-600 absolute -top-3 -right-4 flex h-5 w-5 items-center justify-center rounded-full text-xs leading-5 font-medium text-white',
                    hasMoreThanNinetyNineComments && '!text-[8px]',
                  )}
                >
                  {hasMoreThanNinetyNineComments ? '99+' : unresolvedCommentCount}
                </div>
              </Show>
            </div>
          </Show>

          <Show when={!hasComments && isReviewStatus}>
            <TooltipedIcon
              size={0.9}
              className={iconClasses()}
              data-testid={getId('add-comment', name)}
              path={mdiCommentPlusOutline}
              onClick={onCommentClick}
              content="Add a comment"
            />
          </Show>
        </Show>

        <Show when={isEditable}>
          <TooltipedIcon
            size={0.9}
            className={iconClasses()}
            containerClassName={'hidden group-hover:block'}
            content={
              isBlockOrInlineAttachment(block) === 'attachment' ? 'Rename' : `Edit ${isClause ? 'clause' : 'block'}`
            }
            data-testid={getId('edit', name)}
            onClick={onEdit}
            path={mdiPencil}
          />
        </Show>

        <Show when={isEditable && isTemplate && !(isBlockOrInlineAttachment(block) === 'attachment')}>
          <TooltipedIcon
            size={0.9}
            className={iconClasses()}
            data-testid={getId('visibility', name)}
            path={mdiCog}
            onClick={onOpenVisibilityModal}
            content="Configure visibility"
          />
        </Show>

        <Show when={!!blockLibraryId && canViewInLibrary}>
          <TooltipedIcon
            size={0.9}
            className={iconClasses()}
            data-testid={getId('view-block-in-library', name)}
            path={mdiOpenInNew}
            onClick={onViewInLibrary}
            content={`View ${isClause ? 'clause' : 'block'} in library`}
          />
        </Show>

        <Show when={isDeletable}>
          <TooltipedIcon
            size={0.9}
            className={iconClasses()}
            containerClassName={'hidden group-hover:block'}
            content={
              isBlockOrInlineAttachment(block) === 'attachment' ? 'Remove' : isClause ? 'Remove clause' : 'Remove block'
            }
            data-testid={getId('delete', name)}
            onClick={onDelete}
            path={mdiTrashCanOutline}
          />
        </Show>
      </div>
    </Container>
  );
};
